import React, { useState } from "react";
import { Skeleton } from "../ui/skeleton";
import { useTranslation } from "react-i18next";
import { indexStore } from "src/store/indexStore";
import { IMG_URl } from "src/common/img_url";
import { AlertDialogs } from "../Dialgos/Alter-Daylogs";
import EditNotificationModel from "../Notifications/EditNotificationModel";
import { SelectBox } from "../selectbox/Selectbox";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Bell,
  Send,
  Users,
  UserPlus,
  Globe,
  Image as ImageIcon,
  Edit,
  Trash2,
  Loader2,
} from "lucide-react";

function NotificationCard({ notification }) {
  const {
    lang,
    description,
    setNotification,
    deleteNotificationFunction,
    publishNotificationFunction,
  } = indexStore();
  const { t } = useTranslation();
  const [target, setTarget] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);

  const title = lang === "ar" ? notification.title_ar : notification.title_en;

  const handleDelete = () => deleteNotificationFunction(notification.id);
  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      await publishNotificationFunction(notification.id, target);
      setTarget(""); // Reset target after successful publish
    } catch (error) {
      // Error is already handled by showResponseMessage
      console.error("Failed to publish notification:", error);
    } finally {
      setIsPublishing(false);
    }
  };

  const getTargetIcon = (target) => {
    switch (target) {
      case "all":
        return Globe;
      case "registered":
        return UserPlus;
      case "guests":
        return Users;
      default:
        return Users;
    }
  };

  const getTargetLabel = (target) => {
    switch (target) {
      case "all":
        return t("gloabl.all");
      case "registered":
        return t("gloabl.registered");
      case "guests":
        return t("gloabl.guests");
      default:
        return t("gloabl.target");
    }
  };

  return (
    <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden border-2 hover:border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-4">
          {/* Image */}
          <div className="relative shrink-0">
            {notification.image ? (
              <img
                src={`${IMG_URl}/${notification.image}`}
                alt={title}
                className="w-20 h-20 object-cover rounded-xl border-2 shadow-sm"
              />
            ) : (
              <div className="w-20 h-20 rounded-xl border-2 bg-muted flex items-center justify-center">
                <ImageIcon className="w-8 h-8 text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Title and Description */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="text-lg font-bold text-foreground line-clamp-2">
                {title}
              </h3>
              <div className="flex gap-1 shrink-0">
                <EditNotificationModel
                  trigger={
                    <button
                      onClick={() => setNotification(notification)}
                      className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                      title={t("gloabl.edit")}
                    >
                      <Edit className="w-4 h-4 text-muted-foreground" />
                    </button>
                  }
                />
                <AlertDialogs
                  trigger={
                    <button
                      className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                      title={t("gloabl.delete")}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  }
                  variant="icon"
                  onClickOk={handleDelete}
                  description={<span>{t("gloabl.deleteMessage")}</span>}
                  title={`${t("gloabl.delete")} ${t(`gloabl.notification`)}`}
                />
              </div>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {notification[description]}
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Target Selection and Publish */}
        <div className="space-y-3 p-4 rounded-lg bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 border border-blue-200/50 dark:border-blue-800/50">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-semibold text-foreground">
              {t("gloabl.publish_to") || "Publish To"}
            </span>
          </div>
          <div className="flex gap-2 items-center">
            <SelectBox
              placeholder={t("gloabl.target")}
              valueKey="value"
              data={[
                { label: t("gloabl.all"), value: "all" },
                { label: t("gloabl.registered"), value: "registered" },
                { label: t("gloabl.guests"), value: "guests" },
              ]}
              onValueChange={(value) => setTarget(value)}
              label="label"
              className="flex-1"
            />
            <Button
              onClick={handlePublish}
              disabled={!target || isPublishing}
              className="flex-shrink-0 gap-2"
              size="sm"
            >
              {isPublishing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("gloabl.processing")}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  {t("gloabl.publish")}
                </>
              )}
            </Button>
          </div>
          {target && (
            <Badge
              variant="outline"
              className="w-fit bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300"
            >
              {(() => {
                const TargetIcon = getTargetIcon(target);
                return <TargetIcon className="w-3 h-3 mr-1" />;
              })()}
              {getTargetLabel(target)}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default NotificationCard;

// Loader
export const NotificationCardLoading = () => (
  <Card>
    <CardHeader>
      <div className="flex gap-4">
        <Skeleton className="w-20 h-20 rounded-xl shrink-0" />
        <div className="flex-1 flex flex-col gap-2">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      </div>
    </CardHeader>
    <CardContent>
      <div className="space-y-3">
        <Skeleton className="h-4 w-24" />
        <div className="flex gap-2">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-24" />
        </div>
      </div>
    </CardContent>
  </Card>
);
